import {
  QUOTE_PRICE,
  DIRECT_OUTBOUND_AVAILABLE,
  DIRECT_INBOUND_AVAILABLE,
  DIRECT,
  INDIRECT,
  DIRECT_OUTBOUND,
  INDIRECT_OUTBOUND,
  DIRECT_INBOUND,
  INDIRECT_INBOUND,
  TRACE_REFS,
} from '../constants/dataServiceConstants';
import * as dataConstants from '../constants/dataTypeConstants';
import {
  ALL_DEPARTURE_TIMES,
  type DepartureTime,
} from '../constants/departureTimeConstants';

import type {
  HasDepartureTimes,
  MonthDataGridElement,
  PriceEntry,
} from '../../selectors/types';

const isEntryDirect = (
  gridEntry: MonthDataGridElement,
  isReturn: boolean,
): boolean => {
  const directOutboundAvailable = gridEntry[DIRECT_OUTBOUND_AVAILABLE] === true;
  const directInboundAvailable =
    !isReturn || gridEntry[DIRECT_INBOUND_AVAILABLE] === true;
  return directOutboundAvailable && directInboundAvailable;
};

const getSpecificPrice = (
  gridEntry: MonthDataGridElement,
  direction: string,
  isDirect: boolean,
): number | null => {
  if (isDirect && direction === dataConstants.OUTBOUND_DIRECTION) {
    const directOutboundEntry = gridEntry[DIRECT_OUTBOUND];
    if (!directOutboundEntry) return null;
    return directOutboundEntry[QUOTE_PRICE] ?? null;
  }
  if (isDirect && direction === dataConstants.INBOUND_DIRECTION) {
    const directInboundEntry = gridEntry[DIRECT_INBOUND];
    if (!directInboundEntry) return null;
    return directInboundEntry[QUOTE_PRICE] ?? null;
  }
  if (!isDirect && direction === dataConstants.OUTBOUND_DIRECTION) {
    const indirectOutboundEntry = gridEntry[INDIRECT_OUTBOUND];
    if (!indirectOutboundEntry) return null;
    return indirectOutboundEntry[QUOTE_PRICE] ?? null;
  }
  if (!isDirect && direction === dataConstants.INBOUND_DIRECTION) {
    const indirectInboundEntry = gridEntry[INDIRECT_INBOUND];
    if (!indirectInboundEntry) return null;
    return indirectInboundEntry[QUOTE_PRICE] ?? null;
  }
  return null;
};

const cheapestOfSelectedTimes = (
  subEntry: PriceEntry & HasDepartureTimes,
  departureTimes: DepartureTime[],
): DepartureTime | undefined => {
  // look in available departure time buckets of quote for time with
  // cheapest price out of the selected times
  const lookup = subEntry.DepartureTimes;
  if (!lookup || !departureTimes || !departureTimes.length) return undefined;
  let result;
  let cheapestPrice;
  for (const bucket of departureTimes) {
    const priceAtTime = lookup[bucket]?.Price;
    if (priceAtTime && (!cheapestPrice || cheapestPrice > priceAtTime)) {
      result = bucket;
      cheapestPrice = priceAtTime;
    }
  }
  return result;
};

// no departure times in response at all is different from no matching departure times
const hasKnownDepartureTimes = (
  subEntry: PriceEntry & HasDepartureTimes,
): boolean =>
  !!(subEntry?.DepartureTimes && Object.keys(subEntry.DepartureTimes).length);

const summariseForCheapestTime = (
  subEntry: (PriceEntry & HasDepartureTimes) | undefined,
  departureTimes: DepartureTime[] | undefined,
) => {
  // if there are selected departure times, use them to find the cheapest price and
  // corresponding traces for available times that are included in the selection
  const allTimes = ALL_DEPARTURE_TIMES.length;
  if (
    subEntry &&
    departureTimes?.length &&
    departureTimes.length !== allTimes
  ) {
    const cheapestTime = cheapestOfSelectedTimes(subEntry, departureTimes);
    // if response DOES HAVE departure times but none match selection, return null
    if (hasKnownDepartureTimes(subEntry) && !cheapestTime) {
      return null;
    }
    if (cheapestTime && subEntry?.DepartureTimes) {
      const atCheapestTime = subEntry.DepartureTimes[cheapestTime];
      if (!atCheapestTime) return subEntry; // can't happen, but not provable to typescript
      return {
        [QUOTE_PRICE]: atCheapestTime[QUOTE_PRICE],
        [TRACE_REFS]: atCheapestTime[TRACE_REFS] ?? [],
      };
    }
  }
  // if not, or there are no known available times, fall back to top-level price
  return subEntry;
};

const getSpecificPriceWithTimes = (
  gridEntry: MonthDataGridElement,
  direction: string,
  isDirect: boolean,
  departureTimes?: DepartureTime[],
): number | null => {
  let specificEntry;
  if (isDirect && direction === dataConstants.OUTBOUND_DIRECTION)
    specificEntry = gridEntry[DIRECT_OUTBOUND];
  else if (isDirect && direction === dataConstants.INBOUND_DIRECTION)
    specificEntry = gridEntry[DIRECT_INBOUND];
  else if (!isDirect && direction === dataConstants.OUTBOUND_DIRECTION)
    specificEntry = gridEntry[INDIRECT_OUTBOUND];
  else if (!isDirect && direction === dataConstants.INBOUND_DIRECTION)
    specificEntry = gridEntry[INDIRECT_INBOUND];
  else return null;

  if (!specificEntry) return null;
  // Outbound might have prices specific to the selected departure times
  if (direction === dataConstants.OUTBOUND_DIRECTION) {
    specificEntry = summariseForCheapestTime(specificEntry, departureTimes);
  }
  return specificEntry?.[QUOTE_PRICE] ?? null;
};

export const getDirectTotalPrice = (
  gridEntry: MonthDataGridElement,
): number | null => {
  const directEntry = gridEntry[DIRECT];
  if (directEntry) {
    return directEntry[QUOTE_PRICE] ?? null;
  }
  return null;
};

export const getTotalPrice = (
  gridEntry: MonthDataGridElement,
): number | null => {
  const indirectPrice = gridEntry[INDIRECT]?.[QUOTE_PRICE] ?? null;
  const directPrice = gridEntry[DIRECT]?.[QUOTE_PRICE] ?? null;

  if (indirectPrice === null) return directPrice;
  if (directPrice === null) return indirectPrice;
  return Math.min(indirectPrice, directPrice);
};

const getSummaryData = (
  gridEntry: MonthDataGridElement,
  preferDirects: boolean,
) => {
  if (preferDirects) {
    return gridEntry[DIRECT];
  }

  const indirect = gridEntry[INDIRECT];
  const direct = gridEntry[DIRECT];

  if (!indirect) return direct;
  if (!direct) return indirect;

  const directPrice = direct[QUOTE_PRICE];
  const indirectPrice = indirect[QUOTE_PRICE];

  return directPrice < indirectPrice ? direct : indirect;
};

const getSummaryDataWithTimes = (
  gridEntry: MonthDataGridElement,
  preferDirects: boolean,
  departureTimes?: DepartureTime[],
) => {
  const direct = summariseForCheapestTime(gridEntry[DIRECT], departureTimes);
  const indirect = preferDirects
    ? null
    : summariseForCheapestTime(gridEntry[INDIRECT], departureTimes);

  if (!indirect) return direct;
  if (!direct) return indirect;

  return direct.Price < indirect.Price ? direct : indirect;
};

export default {
  getSpecificPrice,
  getSpecificPriceWithTimes,
  getSummaryData,
  getSummaryDataWithTimes,
  getTotalPrice,
  getDirectTotalPrice,
  isEntryDirect,
};
